import { IListPageRequest } from "types/globalTypes";

export type MockPageInfo = IListPageRequest & Record<string, unknown>;

const SCROLL_QUERY_SEP = "\x1e";
const SCROLL_QUERY_PENDING = "\0__pending_academy_filters__\0";

export const DEMO_ACADEMY_ID = "00000000-0000-0000-0000-000000000010";
export const DEMO_ACADEMY_NORTH_ID = "00000000-0000-0000-0000-000000000011";
export const DEMO_ACADEMY_SOUTH_ID = "00000000-0000-0000-0000-000000000012";
export const DEMO_ACADEMY_WOMEN_ID = "00000000-0000-0000-0000-000000000013";

export const DEMO_GROUP_U15A = "00000000-0000-0000-0000-000000001601";
export const DEMO_GROUP_U13B = "00000000-0000-0000-0000-000000001602";
export const DEMO_GROUP_U17 = "00000000-0000-0000-0000-000000001603";
export const DEMO_GROUP_U14W = "00000000-0000-0000-0000-000000001701";

export const extractSearchQuery = (pageInfo: MockPageInfo): string => {
  const raw = String(pageInfo.SearchQuery ?? "").trim();
  if (!raw || raw === SCROLL_QUERY_PENDING) {
    return "";
  }

  const sepIndex = raw.indexOf(SCROLL_QUERY_SEP);
  const search = sepIndex === -1 ? raw : raw.slice(0, sepIndex);
  return search.trim().toLowerCase();
};

export const normalizeIds = (value: unknown): string[] => {
  if (value == null || value === "") {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => normalizeIds(item));
  }

  const stringValue = String(value).trim();
  if (stringValue.includes(",")) {
    return stringValue
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
  }

  return [stringValue];
};

export const matchesAcademyIds = (
  itemAcademyId: string | undefined,
  pageInfo: MockPageInfo
): boolean => {
  const ids = normalizeIds(pageInfo.AcademyIds ?? pageInfo.AcademyId);
  if (!ids.length) {
    return true;
  }

  return itemAcademyId ? ids.includes(String(itemAcademyId)) : false;
};

export const matchesOptionalValue = (
  itemValue: unknown,
  filterValue: unknown
): boolean => {
  if (filterValue === null || filterValue === undefined || filterValue === "") {
    return true;
  }

  return String(itemValue) === String(filterValue);
};

export const matchesOptionalNumber = (
  itemValue: unknown,
  filterValue: unknown
): boolean => {
  if (filterValue === null || filterValue === undefined || filterValue === "") {
    return true;
  }

  return Number(itemValue) === Number(filterValue);
};

export const matchesStatusIn = (itemStatus: unknown, statuses: unknown): boolean => {
  const list = normalizeIds(statuses);
  if (!list.length) {
    return true;
  }

  return list.includes(String(itemStatus));
};

export const matchesGroupIds = (
  itemGroupIds: string[] | undefined,
  pageInfo: MockPageInfo
): boolean => {
  const ids = normalizeIds(pageInfo.GroupIds ?? pageInfo.GroupId);
  if (!ids.length) {
    return true;
  }

  return !!itemGroupIds?.some((groupId) => ids.includes(String(groupId)));
};

export const matchesDateRange = (
  dateValue: Date | string | null | undefined,
  from: unknown,
  to: unknown
): boolean => {
  if (!from && !to) {
    return true;
  }

  if (!dateValue) {
    return false;
  }

  const date = new Date(dateValue);
  if (isNaN(date.getTime())) {
    return false;
  }

  if (from) {
    const fromDate = new Date(String(from));
    if (!isNaN(fromDate.getTime()) && date < fromDate) {
      return false;
    }
  }

  if (to) {
    const toValue = String(to);
    const toDate =
      toValue.length === 10
        ? new Date(`${toValue}T23:59:59.999`)
        : new Date(toValue);
    if (!isNaN(toDate.getTime()) && date > toDate) {
      return false;
    }
  }

  return true;
};

export const matchesHasAppliedPass = (
  itemValue: boolean | undefined,
  filterValue: unknown
): boolean => {
  if (filterValue === null || filterValue === undefined) {
    return true;
  }

  return Boolean(itemValue) === Boolean(filterValue);
};

export const matchesMaxDistance = (
  distance: number | undefined,
  maxArea: unknown
): boolean => {
  if (maxArea === null || maxArea === undefined || String(maxArea).trim() === "") {
    return true;
  }

  if (distance == null) {
    return true;
  }

  return distance <= Number(maxArea);
};

export const applyMockItemFilters = <T>(
  items: T[],
  pageInfo: MockPageInfo,
  rules: Array<(item: T, pageInfo: MockPageInfo) => boolean>
): T[] => items.filter((item) => rules.every((rule) => rule(item, pageInfo)));
